import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
  AddClientRequest,
  ClientDocumentMeta,
  ClientNote,
  ClientWithStats,
  EditClientRequest,
} from "./client.interface";
import { endOfDay } from "../shared/utils/trial.util";
import { Observable } from "rxjs";
import { AddSubdomainRequest } from "./client.interface";

@Injectable({
  providedIn: "root",
})
export class ClientService {
  constructor(private http: HttpClient) {}

  getAll = () => this.http.get<ClientWithStats[]>("/admin/client");

  getOne = (clientId: string) =>
    this.http.get<ClientWithStats>(
      `/admin/client/${encodeURIComponent(clientId)}`,
    );

  add = (request: AddClientRequest) =>
    this.http.post("/admin/client", {
      ...request,
      trialFinishDate: endOfDay(request.trialFinishDate),
    });

  remove = (clientId: string) =>
    this.http.delete(`/admin/client/${encodeURIComponent(clientId)}`);

  edit = (clientId: string, request: EditClientRequest) =>
    this.http.put(`/admin/client/${encodeURIComponent(clientId)}`, {
      ...request,
      trialFinishDate: endOfDay(request.trialFinishDate),
    });

  addSubdomain(
    clientId: string,
    request: AddSubdomainRequest,
  ): Observable<unknown> {
    return this.http.post(
      `/admin/client/${encodeURIComponent(clientId)}/subdomain`,
      {
        ...request,
        trialFinishDate: request.trialFinishDate
          ? endOfDay(request.trialFinishDate)
          : undefined,
      },
    );
  }

  listNotes = (clientId: string) =>
    this.http.get<ClientNote[]>(
      `/admin/client/${encodeURIComponent(clientId)}/notes`,
    );

  createNote = (clientId: string, body: string) =>
    this.http.post<ClientNote>(
      `/admin/client/${encodeURIComponent(clientId)}/notes`,
      { body },
    );

  updateNote = (clientId: string, clientNoteId: string, body: string) =>
    this.http.put<ClientNote>(
      `/admin/client/${encodeURIComponent(clientId)}/notes/${encodeURIComponent(clientNoteId)}`,
      { body },
    );

  deleteNote = (clientId: string, clientNoteId: string) =>
    this.http.delete(
      `/admin/client/${encodeURIComponent(clientId)}/notes/${encodeURIComponent(clientNoteId)}`,
    );

  listDocuments = (clientId: string) =>
    this.http.get<ClientDocumentMeta[]>(
      `/admin/client/${encodeURIComponent(clientId)}/documents`,
    );

  uploadDocument = (clientId: string, file: File, name: string) => {
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("name", name);
    return this.http.post<ClientDocumentMeta>(
      `/admin/client/${encodeURIComponent(clientId)}/documents`,
      formData,
    );
  };

  downloadDocument = (clientId: string, clientDocumentId: string) =>
    this.http.get(
      `/admin/client/${encodeURIComponent(clientId)}/documents/${encodeURIComponent(clientDocumentId)}/download`,
      { responseType: "blob" },
    );

  deleteDocument = (clientId: string, clientDocumentId: string) =>
    this.http.delete(
      `/admin/client/${encodeURIComponent(clientId)}/documents/${encodeURIComponent(clientDocumentId)}`,
    );

  sendClientOnboardingLink(
    email: string,
    domain: string,
    lang: string = "en",
  ): Observable<{ signInLink: string }> {
    return this.http.post<{ signInLink: string }>(
      "/webflow/client-onboarding-link",
      {
        email,
        domain,
        lang,
      },
    );
  }

  runExpiredTrialCron(): Observable<unknown> {
    return this.http.post("/expired-trial", {});
  }

  assignSuperAdminToAllDomains(email: string): Observable<unknown> {
    return this.http.post("/admin/client/super-admin/assign-all", { email });
  }

  removeSuperAdminFromAllDomains(email: string): Observable<unknown> {
    return this.http.post("/admin/client/super-admin/remove-all", { email });
  }
}
